import React, { useMemo, useRef, forwardRef } from 'react';

import { BaseTextInputProps } from '../shared-types';
import BaseInput from '../BaseInput/BaseInput';
import BaseInputIcon from '../BaseInput/BaseInputIcon';
import InputControl from '../InputControl/InputControl';
import { useForkRef, useInputComponentIdAndLabelData } from '../utils';
import { SxProp } from '../style/sxProp';

export type Props = {
  sx?: SxProp;
} & BaseTextInputProps;

const SearchInput = forwardRef<HTMLInputElement, Props>(
  ({ label, sx, value, onChange, 'aria-label': ariaLabel, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>();

    const handleRef: React.Ref<HTMLInputElement> = useForkRef(ref, inputRef);
    const adornmentRight = useMemo(
      () =>
        value ? (
          <BaseInputIcon
            icon="clear"
            onClick={() => {
              if (onChange) {
                onChange('');
              }
              inputRef.current?.focus();
            }}
          />
        ) : null,
      [value, onChange]
    );

    const {
      id,
      containerId,
      labelId,
      'aria-labelledby': ariaLabelledby,
    } = useInputComponentIdAndLabelData('SearchInput', label, ariaLabel);

    return (
      <InputControl id={containerId} label={label} labelId={labelId} sx={sx}>
        <BaseInput
          {...props}
          ref={handleRef}
          label={label}
          componentName="SearchInput"
          value={value}
          error={false}
          onChange={(event) => onChange && onChange(event.target.value)}
          adornmentRight={adornmentRight}
          adornmentLeft={<BaseInputIcon icon="search" aria-hidden />}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby}
          data-testid={id}
        />
      </InputControl>
    );
  }
);

export default SearchInput;
